import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { publicAPI, channelPartnerAuthAPI } from '../../services/api';
import { Button, Spinner } from '../UI';
import { HiOutlineShieldCheck, HiOutlineUpload, HiOutlineDocumentText } from 'react-icons/hi';

const MAX_DOC_SIZE = 10 * 1024 * 1024; // 10MB, matches the backend limit

function isAllowedDocType(file) {
  return /^(image\/|application\/pdf)/.test(file.type);
}

const UploadField = ({ file, onChange }) => {
  const [localError, setLocalError] = useState('');

  const handleSelect = (selected) => {
    if (!selected) {
      setLocalError('');
      onChange(null);
      return;
    }
    if (!isAllowedDocType(selected)) {
      setLocalError('Only images (JPG, PNG, etc.) and PDFs are allowed');
      onChange(null);
      return;
    }
    if (selected.size > MAX_DOC_SIZE) {
      setLocalError('File too large — max 10MB');
      onChange(null);
      return;
    }
    setLocalError('');
    onChange(selected);
  };

  return (
    <div>
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-600 transition-colors hover:border-primary-400 hover:bg-primary-50">
        <HiOutlineUpload className="h-4 w-4 shrink-0 text-primary-600" />
        <span className="flex-1 truncate">
          {file ? (
            <span className="inline-flex items-center gap-1.5 text-secondary-900">
              <HiOutlineDocumentText className="h-3.5 w-3.5" /> {file.name}
            </span>
          ) : (
            'Click to upload (image or PDF, max 10MB)'
          )}
        </span>
        <input
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleSelect(e.target.files?.[0] || null)}
        />
      </label>
      {localError && <p className="mt-1 text-xs text-danger-600">{localError}</p>}
    </div>
  );
};

const PartnerKycForm = ({ onSubmitted }) => {
  const [requirementsByCountry, setRequirementsByCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('IN');
  const [files, setFiles] = useState({}); // { [leafDocKey]: File }
  const [choiceSelections, setChoiceSelections] = useState({}); // { [groupKey]: optionKey }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    publicAPI.getKycRequirements()
      .then((res) => {
        if (cancelled) return;
        setRequirementsByCountry(res.data?.data || {});
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load KYC requirements. Please refresh and try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!requirementsByCountry) {
    return <p className="text-sm text-danger-600 text-center">{error}</p>;
  }

  const requirements = requirementsByCountry[country] || requirementsByCountry.OTHER;
  const selectedOptionKey = (doc) => choiceSelections[doc.key] || doc.choiceOf[0].key;

  const onFileChange = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
  };

  const onCountryChange = (code) => {
    setCountry(code);
    setFiles({});
    setChoiceSelections({});
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const missing = requirements.documents.filter((d) => {
      if (!d.required) return false;
      if (d.choiceOf) return !files[selectedOptionKey(d)];
      return !files[d.key];
    });
    if (missing.length > 0) {
      setError(`Missing required document(s): ${missing.map((d) => d.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('country', country);
      const flatDocOptions = requirements.documents.flatMap((d) => (d.choiceOf ? d.choiceOf : [d]));
      flatDocOptions.forEach((d) => {
        if (files[d.key]) formData.append(`doc_${d.key}`, files[d.key]);
      });

      await channelPartnerAuthAPI.submitKyc(formData);
      toast.success('KYC submitted — verification is in progress');
      onSubmitted();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="text-left">
      <div className="flex items-center gap-2 text-primary-600">
        <HiOutlineShieldCheck className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-wider">KYC Verification</span>
      </div>
      <h2 className="mt-1 text-xl font-bold text-secondary-900">Complete Your KYC</h2>
      <p className="mt-1 text-sm text-secondary-500">
        Required before your payouts can be released — this usually takes 1–2 business days to verify.
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      <div className="mt-5">
        <span className="mb-1.5 block text-sm font-medium text-secondary-700">Country</span>
        <select
          value={country}
          onChange={(e) => onCountryChange(e.target.value)}
          className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {Object.entries(requirementsByCountry).map(([code, c]) => (
            <option key={code} value={code}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-5 space-y-4">
        {requirements.documents.map((doc) =>
          doc.choiceOf ? (
            <div key={doc.key}>
              <span className="mb-1.5 block text-sm font-medium text-secondary-700">
                {doc.label} {doc.required && <span className="text-danger-500">*</span>}
              </span>
              <div className="mb-2 flex flex-wrap gap-2">
                {doc.choiceOf.map((opt) => {
                  const active = selectedOptionKey(doc) === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setChoiceSelections((prev) => ({ ...prev, [doc.key]: opt.key }))}
                      className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                        active
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-secondary-300 bg-white text-secondary-500 hover:border-primary-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <UploadField
                file={files[selectedOptionKey(doc)]}
                onChange={(file) => onFileChange(selectedOptionKey(doc), file)}
              />
            </div>
          ) : (
            <div key={doc.key}>
              <span className="mb-1.5 block text-sm font-medium text-secondary-700">
                {doc.label} {doc.required && <span className="text-danger-500">*</span>}
              </span>
              <UploadField file={files[doc.key]} onChange={(file) => onFileChange(doc.key, file)} />
            </div>
          )
        )}
      </div>

      <Button type="submit" variant="primary" loading={submitting} disabled={submitting} className="mt-6 w-full">
        Submit KYC Documents
      </Button>
    </form>
  );
};

export default PartnerKycForm;
